/**
 * Edit a conference-intake form — a survey (questions) or a consent form
 * (sections, choices, signature labels). Saving an existing form creates a
 * new version under the same family; responses already collected keep
 * pointing at the version they answered. A blank form is created fresh.
 *
 * Lists (options, parts) are edited as one-per-line text and turned back
 * into arrays on save, so typing a new line never loses what's there.
 */
import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import { extractErrorMessage } from '../../api/errors';
import {
  createIntakeForm,
  saveIntakeForm,
  type ConsentDefinition,
  type IntakeForm,
  type IntakeFormKind,
  type QuestionType,
  type SurveyDefinition,
  type SurveyQuestion,
  isPostSurvey,
  OR_SURVEY_TYPES,
  type PostSurveyKind,
} from '../../api/intake';
import { SearchableSelect } from '../SearchableSelect';

/** An existing form (edit → new version) or a new blank one. */
export type EditorTarget = IntakeForm | { kind: IntakeFormKind; blank: true };

export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  heading: 'Heading',
  text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  number: 'Number',
  percent: 'Percent',
  single: 'Single choice',
  multi: 'Multiple choice',
  yesno: 'Yes / No',
  date: 'Date',
  month_year: 'Month / year',
  percent_split: 'Percent split',
};
const TYPE_OPTIONS = (Object.keys(QUESTION_TYPE_LABEL) as QuestionType[]).map((t) => ({
  value: t,
  label: QUESTION_TYPE_LABEL[t],
}));
const ALWAYS = [{ value: '', label: 'Always show' }];

/** "Years in practice?" → "years_in_practice". */
export function slugify(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64) || 'q'
  );
}

function uniqueId(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  let n = 2;
  while (taken.includes(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

const lines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

function move<T>(list: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

let nextKey = 1;
const newKey = () => nextKey++;

// ── Drafts ───────────────────────────────────────────────────────────────

interface QuestionDraft {
  key: number;
  id: string;
  /** False while the id still follows the label (new questions only). */
  idEdited: boolean;
  type: QuestionType;
  label: string;
  help: string;
  required: boolean;
  optionsText: string;
  allowOther: boolean;
  partsText: string;
  min: string;
  max: string;
  /** show_if points at another draft by key, so renaming its id follows. */
  showIfKey: number | null;
  showIfEquals: string;
}

interface SectionDraft {
  key: number;
  heading: string;
  body: string;
}

interface ChoiceDraft {
  key: number;
  id: string;
  label: string;
  optionsText: string;
  initials: boolean;
}

function blankQuestion(): QuestionDraft {
  return {
    key: newKey(),
    id: '',
    idEdited: false,
    type: 'text',
    label: '',
    help: '',
    required: false,
    optionsText: '',
    allowOther: false,
    partsText: '',
    min: '',
    max: '',
    showIfKey: null,
    showIfEquals: '',
  };
}

function questionDrafts(qs: SurveyQuestion[]): QuestionDraft[] {
  const drafts = qs.map((q) => ({
    ...blankQuestion(),
    id: q.id,
    idEdited: true,
    type: q.type,
    label: q.label,
    help: q.help ?? '',
    required: !!q.required,
    optionsText: (q.options ?? []).join('\n'),
    allowOther: !!q.allow_other,
    partsText: (q.parts ?? []).join('\n'),
    min: q.min == null ? '' : String(q.min),
    max: q.max == null ? '' : String(q.max),
  }));
  qs.forEach((q, i) => {
    if (!q.show_if) return;
    const target = drafts.find((d) => d.id === q.show_if!.id);
    if (target) {
      drafts[i].showIfKey = target.key;
      drafts[i].showIfEquals = q.show_if.equals;
    }
  });
  return drafts;
}

function toQuestions(drafts: QuestionDraft[]): SurveyQuestion[] {
  return drafts.map((d) => {
    const q: SurveyQuestion = { id: d.id.trim(), type: d.type, label: d.label.trim() };
    if (d.help.trim()) q.help = d.help.trim();
    if (d.required && d.type !== 'heading') q.required = true;
    if (d.type === 'single' || d.type === 'multi') {
      q.options = lines(d.optionsText);
      if (d.allowOther) q.allow_other = true;
    }
    if (d.type === 'percent_split') q.parts = lines(d.partsText);
    if (d.type === 'number') {
      if (d.min.trim() !== '') q.min = Number(d.min);
      if (d.max.trim() !== '') q.max = Number(d.max);
    }
    const dep = d.showIfKey == null ? null : drafts.find((o) => o.key === d.showIfKey);
    if (dep && d.showIfEquals.trim()) q.show_if = { id: dep.id.trim(), equals: d.showIfEquals.trim() };
    return q;
  });
}

const BLANK_SURVEY: SurveyDefinition = { title: '', questions: [] };
const BLANK_CONSENT: ConsentDefinition = {
  title: '',
  sections: [{ heading: '', body: '' }],
  choices: [],
  signature_label: 'Signature of participant',
  printed_name_label: 'Printed name of participant',
};

// ── Editor ───────────────────────────────────────────────────────────────

export function IntakeFormEditor({
  open,
  tenantId,
  form,
  readOnly = false,
  onClose,
  onSaved,
}: {
  open: boolean;
  tenantId: string;
  form: EditorTarget;
  /** View only — no saving (read-only admins). */
  readOnly?: boolean;
  onClose: () => void;
  onSaved: (form: IntakeForm) => void;
}) {
  const existing = 'blank' in form ? null : form;
  const kind = form.kind;
  const isSurvey = kind !== 'consent';
  // POST-SURVEYS: a post survey can be re-flagged OR <-> sim on save.
  const [postKind, setPostKind] = useState<PostSurveyKind | null>(
    isPostSurvey(kind) ? kind : null,
  );
  const survey = isSurvey
    ? ((existing?.definition as SurveyDefinition) ?? BLANK_SURVEY)
    : null;
  const consent =
    kind === 'consent' ? ((existing?.definition as ConsentDefinition) ?? BLANK_CONSENT) : null;

  const [name, setName] = useState(existing?.name ?? '');
  // Header fields shared by both kinds (plus survey subtitle / intro and the
  // consent's protocol fields) live in one flat record.
  const [meta, setMeta] = useState<Record<string, string>>(() => {
    const d = (survey ?? consent) as unknown as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const k of [
      'title',
      'subtitle',
      'intro',
      'institution',
      'protocol_director',
      'protocol_title',
      'irb_approval',
      'irb_expiration',
      'copy_note',
      'signature_label',
      'printed_name_label',
    ]) {
      out[k] = typeof d[k] === 'string' ? (d[k] as string) : '';
    }
    return out;
  });
  const [questions, setQuestions] = useState<QuestionDraft[]>(() =>
    survey ? (survey.questions.length ? questionDrafts(survey.questions) : [blankQuestion()]) : [],
  );
  const [sections, setSections] = useState<SectionDraft[]>(() =>
    (consent?.sections ?? []).map((s) => ({ key: newKey(), heading: s.heading ?? '', body: s.body })),
  );
  const [choices, setChoices] = useState<ChoiceDraft[]>(() =>
    (consent?.choices ?? []).map((c) => ({
      key: newKey(),
      id: c.id,
      label: c.label,
      optionsText: c.options.join('\n'),
      initials: c.initials ?? true,
    })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (k: string) => (e: { target: { value: string } }) =>
    setMeta((m) => ({ ...m, [k]: e.target.value }));
  /** Optional text → omitted when blank. */
  const opt = (k: string) => (meta[k].trim() ? meta[k].trim() : undefined);

  const patchQuestion = (key: number, patch: Partial<QuestionDraft>) =>
    setQuestions((qs) =>
      qs.map((q) => {
        if (q.key !== key) return q;
        const next = { ...q, ...patch };
        if ('label' in patch && !q.idEdited) {
          next.id = uniqueId(
            slugify(patch.label ?? ''),
            qs.filter((o) => o.key !== key).map((o) => o.id),
          );
        }
        return next;
      }),
    );

  const removeQuestion = (key: number) =>
    setQuestions((qs) =>
      qs
        .filter((q) => q.key !== key)
        .map((q) => (q.showIfKey === key ? { ...q, showIfKey: null, showIfEquals: '' } : q)),
    );

  const buildDefinition = (): SurveyDefinition | ConsentDefinition => {
    if (isSurvey) {
      return {
        ...survey!,
        title: meta.title.trim(),
        subtitle: opt('subtitle'),
        intro: opt('intro'),
        questions: toQuestions(questions),
      };
    }
    // Existing choices keep their id; new ones get a unique slug of the label.
    const ids = choices.filter((c) => c.id).map((c) => c.id);
    return {
      ...consent!,
      title: meta.title.trim(),
      institution: opt('institution'),
      protocol_director: opt('protocol_director'),
      protocol_title: opt('protocol_title'),
      irb_approval: opt('irb_approval'),
      irb_expiration: opt('irb_expiration'),
      sections: sections.map((s) => ({
        ...(s.heading.trim() ? { heading: s.heading.trim() } : {}),
        body: s.body.trim(),
      })),
      choices: choices.map((c) => {
        let id = c.id;
        if (!id) {
          id = uniqueId(slugify(c.label), ids);
          ids.push(id);
        }
        return { id, label: c.label.trim(), options: lines(c.optionsText), initials: c.initials };
      }),
      copy_note: opt('copy_note'),
      signature_label: meta.signature_label.trim() || BLANK_CONSENT.signature_label,
      printed_name_label: meta.printed_name_label.trim() || BLANK_CONSENT.printed_name_label,
    };
  };

  const save = async () => {
    if (!name.trim()) {
      setError('Give the form a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const definition = buildDefinition();
      const saved = existing
        ? await saveIntakeForm(
            tenantId,
            existing.family_id,
            name.trim(),
            definition,
            postKind && postKind !== kind ? postKind : undefined,
          )
        : await createIntakeForm(tenantId, postKind ?? kind, name.trim(), definition);
      onSaved(saved);
    } catch (e) {
      setError(extractErrorMessage(e, 'could not save the form'));
    } finally {
      setBusy(false);
    }
  };

  const noun =
    postKind === 'or_survey'
      ? 'post survey (OR)'
      : postKind === 'sim_survey'
        ? 'post survey (Sim)'
        : kind === 'survey'
          ? 'pre survey'
          : 'consent form';
  const title = existing
    ? `${readOnly ? 'View' : 'Edit'} ${noun}: ${existing.name}`
    : `New ${noun}`;

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {existing && !readOnly && (
            <Alert severity="info">
              Saving creates version {existing.version + 1}; responses already
              collected keep the version they answered.
            </Alert>
          )}
          {kind === 'consent' && (
            <Alert severity="warning">
              This text must match the consent form your IRB approved.
            </Alert>
          )}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box
            component="fieldset"
            disabled={readOnly || busy}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Stack spacing={2}>
              <TextField
                label="Form name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText="How the form is listed in Settings."
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Title"
                value={meta.title}
                onChange={setField('title')}
                helperText="Shown at the top of the form."
                required
                fullWidth
                size="small"
              />
              {postKind && (
                <TextField
                  select
                  label="Shown"
                  value={postKind}
                  onChange={(e) => setPostKind(e.target.value as PostSurveyKind)}
                  helperText={
                    postKind === 'or_survey'
                      ? 'In the capture app, after each recording. Only the question types the app can show are offered.'
                      : 'On the participant’s phone, from the QR code at the simulation station.'
                  }
                  fullWidth
                  size="small"
                >
                  <MenuItem value="or_survey">Post survey (OR) — in the capture app</MenuItem>
                  <MenuItem value="sim_survey">Post survey (Sim) — from the station QR code</MenuItem>
                </TextField>
              )}
              {isSurvey ? (
                <SurveyFields
                  typeOptions={
                    postKind === 'or_survey'
                      ? TYPE_OPTIONS.filter((o) => OR_SURVEY_TYPES.includes(o.value))
                      : TYPE_OPTIONS
                  }
                  meta={meta}
                  setField={setField}
                  questions={questions}
                  setQuestions={setQuestions}
                  patchQuestion={patchQuestion}
                  removeQuestion={removeQuestion}
                />
              ) : (
                <ConsentFields
                  meta={meta}
                  setField={setField}
                  sections={sections}
                  setSections={setSections}
                  choices={choices}
                  setChoices={setChoices}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button variant="contained" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : existing ? `Save as version ${existing.version + 1}` : 'Create form'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────

type SetField = (k: string) => (e: { target: { value: string } }) => void;

/** Numbered card with move up / down / delete controls. */
function ItemCard({
  label,
  index,
  count,
  onMove,
  onRemove,
  children,
}: {
  label: string;
  index: number;
  count: number;
  onMove: (delta: number) => void;
  onRemove: () => void;
  children: ReactNode;
}) {
  const n = index + 1;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {label} {n}
        </Typography>
        <IconButton
          size="small"
          aria-label={`Move ${label.toLowerCase()} ${n} up`}
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Move ${label.toLowerCase()} ${n} down`}
          disabled={index === count - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Delete ${label.toLowerCase()} ${n}`}
          onClick={onRemove}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack spacing={1.5}>{children}</Stack>
    </Paper>
  );
}

function SurveyFields({
  typeOptions,
  meta,
  setField,
  questions,
  setQuestions,
  patchQuestion,
  removeQuestion,
}: {
  typeOptions: { value: QuestionType; label: string }[];
  meta: Record<string, string>;
  setField: SetField;
  questions: QuestionDraft[];
  setQuestions: (fn: (qs: QuestionDraft[]) => QuestionDraft[]) => void;
  patchQuestion: (key: number, patch: Partial<QuestionDraft>) => void;
  removeQuestion: (key: number) => void;
}) {
  return (
    <>
      <TextField
        label="Subtitle"
        value={meta.subtitle}
        onChange={setField('subtitle')}
        fullWidth
        size="small"
      />
      <TextField
        label="Introduction"
        value={meta.intro}
        onChange={setField('intro')}
        helperText="Shown above the first question."
        multiline
        minRows={2}
        fullWidth
        size="small"
      />
      <Typography variant="overline" color="text.secondary">
        Questions
      </Typography>
      {questions.map((q, i) => (
        <ItemCard
          key={q.key}
          label="Question"
          index={i}
          count={questions.length}
          onMove={(d) => setQuestions((qs) => move(qs, i, d))}
          onRemove={() => removeQuestion(q.key)}
        >
          <QuestionFields
            q={q}
            earlier={questions.slice(0, i)}
            typeOptions={typeOptions}
            patch={(p) => patchQuestion(q.key, p)}
          />
        </ItemCard>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setQuestions((qs) => [...qs, blankQuestion()])}
        >
          Add question
        </Button>
      </Box>
    </>
  );
}

function QuestionFields({
  q,
  earlier,
  typeOptions,
  patch,
}: {
  q: QuestionDraft;
  earlier: QuestionDraft[];
  typeOptions: { value: QuestionType; label: string }[];
  patch: (p: Partial<QuestionDraft>) => void;
}) {
  const idValid = /^[A-Za-z0-9_-]+$/.test(q.id);
  const candidates = earlier.filter((o) => o.type !== 'heading' && o.id);
  const dep = candidates.find((o) => o.key === q.showIfKey) ?? null;
  const depChoices =
    dep?.type === 'yesno'
      ? ['Yes', 'No']
      : dep?.type === 'single' || dep?.type === 'multi'
        ? lines(dep.optionsText)
        : null;
  return (
    <>
      <TextField
        label="Question label"
        value={q.label}
        onChange={(e) => patch({ label: e.target.value })}
        required
        fullWidth
        size="small"
        multiline
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          label="Answer id"
          value={q.id}
          onChange={(e) => patch({ id: e.target.value, idEdited: true })}
          error={!!q.id && !idValid}
          helperText={
            q.id && !idValid
              ? 'Letters, digits, _ and - only.'
              : 'The column name in the responses download.'
          }
          size="small"
          sx={{ flex: 1 }}
        />
        <SearchableSelect
          label="Type"
          value={q.type}
          onChange={(type) => patch({ type })}
          options={typeOptions}
          keepOrder
          sx={{ flex: 1 }}
        />
        {q.type !== 'heading' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={q.required}
                onChange={(e) => patch({ required: e.target.checked })}
              />
            }
            label="Required"
          />
        )}
      </Stack>
      {q.type !== 'heading' && (
        <TextField
          label="Help text"
          value={q.help}
          onChange={(e) => patch({ help: e.target.value })}
          fullWidth
          size="small"
        />
      )}
      {(q.type === 'single' || q.type === 'multi') && (
        <>
          <TextField
            label="Options (one per line)"
            value={q.optionsText}
            onChange={(e) => patch({ optionsText: e.target.value })}
            multiline
            minRows={3}
            fullWidth
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={q.allowOther}
                onChange={(e) => patch({ allowOther: e.target.checked })}
              />
            }
            label="Allow Other (typed answer)"
          />
        </>
      )}
      {q.type === 'percent_split' && (
        <TextField
          label="Parts (one per line)"
          value={q.partsText}
          onChange={(e) => patch({ partsText: e.target.value })}
          helperText="The participant splits 100% across these."
          multiline
          minRows={3}
          fullWidth
          size="small"
        />
      )}
      {q.type === 'number' && (
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Minimum"
            type="number"
            value={q.min}
            onChange={(e) => patch({ min: e.target.value })}
            size="small"
          />
          <TextField
            label="Maximum"
            type="number"
            value={q.max}
            onChange={(e) => patch({ max: e.target.value })}
            size="small"
          />
        </Stack>
      )}
      {q.type !== 'heading' && candidates.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <SearchableSelect
            label="Only show when"
            value={q.showIfKey == null ? '' : String(q.showIfKey)}
            onChange={(v) =>
              patch({ showIfKey: v ? Number(v) : null, showIfEquals: '' })
            }
            options={candidates.map((o) => ({
              value: String(o.key),
              label: o.label || o.id,
            }))}
            pinned={ALWAYS}
            keepOrder
            sx={{ flex: 2 }}
          />
          {dep &&
            (depChoices ? (
              <SearchableSelect
                label="Is answered"
                value={q.showIfEquals}
                onChange={(v) => patch({ showIfEquals: v })}
                options={depChoices.map((c) => ({ value: c, label: c }))}
                keepOrder
                sx={{ flex: 1 }}
              />
            ) : (
              <TextField
                label="Is answered"
                value={q.showIfEquals}
                onChange={(e) => patch({ showIfEquals: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
              />
            ))}
        </Stack>
      )}
    </>
  );
}

function ConsentFields({
  meta,
  setField,
  sections,
  setSections,
  choices,
  setChoices,
}: {
  meta: Record<string, string>;
  setField: SetField;
  sections: SectionDraft[];
  setSections: (fn: (s: SectionDraft[]) => SectionDraft[]) => void;
  choices: ChoiceDraft[];
  setChoices: (fn: (c: ChoiceDraft[]) => ChoiceDraft[]) => void;
}) {
  const text = (k: string, label: string) => (
    <TextField label={label} value={meta[k]} onChange={setField(k)} fullWidth size="small" />
  );
  const patchSection = (key: number, p: Partial<SectionDraft>) =>
    setSections((ss) => ss.map((s) => (s.key === key ? { ...s, ...p } : s)));
  const patchChoice = (key: number, p: Partial<ChoiceDraft>) =>
    setChoices((cs) => cs.map((c) => (c.key === key ? { ...c, ...p } : c)));
  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {text('institution', 'Institution')}
        {text('protocol_director', 'Protocol director')}
      </Stack>
      {text('protocol_title', 'Protocol title')}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {text('irb_approval', 'IRB approval date')}
        {text('irb_expiration', 'IRB expiration date')}
      </Stack>

      <Typography variant="overline" color="text.secondary">
        Sections
      </Typography>
      {sections.map((s, i) => (
        <ItemCard
          key={s.key}
          label="Section"
          index={i}
          count={sections.length}
          onMove={(d) => setSections((ss) => move(ss, i, d))}
          onRemove={() => setSections((ss) => ss.filter((o) => o.key !== s.key))}
        >
          <TextField
            label="Heading"
            value={s.heading}
            onChange={(e) => patchSection(s.key, { heading: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Text"
            value={s.body}
            onChange={(e) => patchSection(s.key, { body: e.target.value })}
            helperText="Blank lines separate paragraphs; start a line with “• ” for a bullet."
            required
            multiline
            minRows={4}
            fullWidth
            size="small"
          />
        </ItemCard>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            setSections((ss) => [...ss, { key: newKey(), heading: '', body: '' }])
          }
        >
          Add section
        </Button>
      </Box>

      <Typography variant="overline" color="text.secondary">
        Choices
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Decisions the participant makes, e.g. whether they may be videotaped.
      </Typography>
      {choices.map((c, i) => (
        <ItemCard
          key={c.key}
          label="Choice"
          index={i}
          count={choices.length}
          onMove={(d) => setChoices((cs) => move(cs, i, d))}
          onRemove={() => setChoices((cs) => cs.filter((o) => o.key !== c.key))}
        >
          <TextField
            label="Choice label"
            value={c.label}
            onChange={(e) => patchChoice(c.key, { label: e.target.value })}
            required
            multiline
            fullWidth
            size="small"
          />
          <TextField
            label="Choice options (one per line)"
            value={c.optionsText}
            onChange={(e) => patchChoice(c.key, { optionsText: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={c.initials}
                onChange={(e) => patchChoice(c.key, { initials: e.target.checked })}
              />
            }
            label="Participant initials their choice"
          />
        </ItemCard>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            setChoices((cs) => [
              ...cs,
              { key: newKey(), id: '', label: '', optionsText: 'Yes\nNo', initials: true },
            ])
          }
        >
          Add choice
        </Button>
      </Box>

      {text('copy_note', 'Copy note')}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {text('signature_label', 'Signature label')}
        {text('printed_name_label', 'Printed-name label')}
      </Stack>
    </>
  );
}
